import React, { useMemo } from 'react'
import { Stack } from '@hub/stack'
import { Text } from '@hub/text'
import { LinkBox, LinkOverlay } from '@hub/link'
import type { HubStyleObject } from '@hub/design-system-base'
import { forwardRef } from '@hub/design-system-base'
import { Imposter } from '@hub/imposter'
import { Badge } from '@hub/badge'
import { ClickAndCollectMediumIcon } from '@hub/icon'
import PotentialLink from '@scentregroup/shared/hub-components/potential-link'
import { generateStoreFrontUrl } from '@scentregroup/shared/helpers/store'
import { CloudinaryImage } from '../cloudinary-image'
import { SGPageTemplateProduct } from '../../../../src/types/page-templates'
import { getProductListingIssue } from '../../helpers/stock-level-issue-messages'

interface ProductCardProps {
  badgeText?: string
  imageRef?: any
  sx?: HubStyleObject
  product: SGPageTemplateProduct
}

export const ProductCard = forwardRef<ProductCardProps, typeof LinkBox>(
  ({ badgeText, imageRef, sx, product }, ref) => {
    const {
      title,
      price,
      discountedPrice,
      percentDiscount,
      shop,
      brand,
      callToAction,
      image,
      issues,
    } = product

    const issue = useMemo(() => getProductListingIssue(issues), [issues])

    const priceToDisplay = discountedPrice || price
    return (
      <LinkBox ref={ref} sx={sx}>
        <Stack gap="spacing-sm">
          <Imposter
            impose={
              badgeText ? (
                <Badge icon={props => <ClickAndCollectMediumIcon {...props} />}>
                  {badgeText}
                </Badge>
              ) : undefined
            }
            position="bottom-left"
            margin="spacing-xs"
          >
            <CloudinaryImage
              imageSetOrImage={{
                url: image.url,
                alt: image.alt,
                type: 'WIDE',
              }}
              resizeMode="pad"
              imageRef={imageRef}
              ratio="2/3"
              sx={{
                '& > img': {
                  outline: '1px solid rgb(217,218,221, 0.7)',
                  outlineOffset: '-1px',
                },
              }}
            />
          </Imposter>
          <Stack gap="spacing-xs">
            <Text fontSize="font-sm">{brand}</Text>
            <LinkOverlay href={callToAction.url}>
              <Text fontSize="font-sm">{title}</Text>
            </LinkOverlay>

            {!issue && (
              <Text fontSize="font-md" tone="strong">
                {/* eslint-disable-next-line no-magic-numbers */}
                {priceToDisplay}
              </Text>
            )}
            {shop.name && (
              <Text
                fontSize="font-sm"
                color="quartz"
                sx={{ marginTop: 'spacing-xs' }}
              >
                Sold by&nbsp;
                <PotentialLink
                  href={generateStoreFrontUrl(shop.retailer?.sys.id)}
                >
                  {shop.name}
                </PotentialLink>
              </Text>
            )}
            {issue && (
              <Text
                color="error"
                fontSize="font-sm"
                sx={{
                  marginTop: 'spacing-sm',
                }}
              >
                {issue.message}
              </Text>
            )}

            {discountedPrice && !issue && (
              <Stack
                sx={{
                  marginTop: 'spacing-sm',
                }}
              >
                <Text
                  fontSize="font-sm"
                  color="quartz"
                  sx={{
                    textDecoration: 'line-through',
                  }}
                >
                  {price}
                </Text>
                <Text fontSize="font-md" tone="strong" color="brand">
                  {percentDiscount}
                </Text>
              </Stack>
            )}
          </Stack>
        </Stack>
      </LinkBox>
    )
  }
)
